import { useState, useEffect, useRef, useCallback } from "react";
import { Client as TwilioClient } from "@twilio/conversations";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useApiCall from "./useApiCall";

/**
 * Manages a Twilio Conversations chat session for a given conversation SID.
 *
 * @param {string|null} conversationSid - The Twilio conversation SID to connect to.
 * @returns {{
 *   messages: Array<{id: string, content: string, author: string, timestamp: Date, media: any[]}>,
 *   sendMessage: (text: string) => Promise<void>,
 *   sendMessageWithMedia: (text: string, files: Array<{file: File, preview: string}>) => Promise<void>,
 *   connectionState: "idle"|"connecting"|"connected"|"disconnected"|"error",
 *   isLoading: boolean,
 *   hasMoreHistory: boolean,
 *   isLoadingMore: boolean,
 *   loadMoreHistory: () => Promise<void>,
 * }}
 */
const useTwilioChat = (conversationSid) => {
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const clientRef = useRef(null);
  const conversationRef = useRef(null);
  const paginatorRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState("idle");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchAccessToken = useCallback(async () => {
    const data = await makeRequest("/v1/twilio/generate_token", {
      method: "POST",
      authTokens: tokens,
    });
    return data?.result?.token;
  }, [makeRequest, tokens]);

  const mapMessage = useCallback((twilioMsg) => {
    let changes;
    try {
      const raw = twilioMsg.attributes;
      const attrs = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
      if (Array.isArray(attrs.changes) && attrs.changes.length > 0) {
        changes = attrs.changes;
      }
    } catch { /* malformed attributes — skip */ }
    return {
      id: twilioMsg.sid,
      content: twilioMsg.body ?? "",
      author: twilioMsg.author ?? "",
      timestamp: twilioMsg.dateCreated,
      media: twilioMsg.attachedMedia ?? [],
      changes,
    };
  }, []);

  const loadHistory = useCallback(async (conversation) => {
    try {
      const paginator = await conversation.getMessages();
      paginatorRef.current = paginator;
      setHasMoreHistory(paginator.hasPrevPage);
      setMessages(paginator.items.map(mapMessage));
    } catch (err) {
      console.error("[useTwilioChat] Failed to load message history:", err);
    }
  }, [mapMessage]);

  const loadMoreHistory = useCallback(async () => {
    if (loadingMoreRef.current || !paginatorRef.current?.hasPrevPage) return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const prevPage = await paginatorRef.current.prevPage();
      paginatorRef.current = prevPage;
      setHasMoreHistory(prevPage.hasPrevPage);
      setMessages((prev) => [...prevPage.items.map(mapMessage), ...prev]);
    } catch (err) {
      console.error("[useTwilioChat] Failed to load more history:", err);
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [mapMessage]);

  useEffect(() => {
    if (!conversationSid) return;

    let cancelled = false;

    const connect = async () => {
      setConnectionState("connecting");
      setIsLoading(true);

      try {
        const accessToken = await fetchAccessToken();
        if (!accessToken) throw new Error("No access token returned from server.");
        if (cancelled) return;

        const client = new TwilioClient(accessToken);
        clientRef.current = client;

        const refreshToken = async () => {
          try {
            const newToken = await fetchAccessToken();
            if (newToken) await client.updateToken(newToken);
          } catch (err) {
            console.error("[useTwilioChat] Token refresh failed:", err);
          }
        };

        client.on("tokenAboutToExpire", refreshToken);
        client.on("tokenExpired", refreshToken);

        // Wait for the client to connect
        await new Promise((resolve, reject) => {
          if (client.connectionState === "connected") return resolve();

          const onStateChange = (state) => {
            if (state === "connected") {
              client.off("connectionStateChanged", onStateChange);
              resolve();
            } else if (state === "denied") {
              client.off("connectionStateChanged", onStateChange);
              reject(new Error("Twilio connection denied. Check your access token."));
            }
          };

          client.on("connectionStateChanged", onStateChange);
        });

        if (cancelled) return;
        setConnectionState("connected");

        // Fetch the existing conversation by SID (already created server-side)
        const conversation = await client.getConversationBySid(conversationSid);
        conversationRef.current = conversation;

        await loadHistory(conversation);

        conversation.on("messageAdded", (twilioMsg) => {
          setMessages((prev) => [...prev, mapMessage(twilioMsg)]);
        });

        conversation.on("messageUpdated", ({ message: twilioMsg }) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === twilioMsg.sid ? mapMessage(twilioMsg) : m))
          );
        });

        client.on("connectionStateChanged", (state) => {
          if (state === "disconnected" || state === "denied") {
            setConnectionState("disconnected");
          }
        });
      } catch (err) {
        if (!cancelled) {
          console.error("[useTwilioChat] Connection error:", err);
          toast.error("Failed to connect to chat. Please refresh and try again.");
          setConnectionState("error");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (conversationRef.current) conversationRef.current.removeAllListeners();
      conversationRef.current = null;
      if (clientRef.current) {
        clientRef.current.removeAllListeners();
        clientRef.current.shutdown();
      }
      clientRef.current = null;
      paginatorRef.current = null;
      loadingMoreRef.current = false;
      setConnectionState("idle");
      setMessages([]);
      setHasMoreHistory(false);
      setIsLoadingMore(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationSid]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text?.trim();
    if (!trimmed) return;

    if (!conversationRef.current) {
      toast.error("Chat is not connected yet.");
      return;
    }

    try {
      await conversationRef.current.sendMessage(trimmed);
    } catch (err) {
      console.error("[useTwilioChat] Failed to send message:", err);
      toast.error("Failed to send message. Please try again.");
    }
  }, []);

  const sendMessageWithMedia = useCallback(async (text, files) => {
    if (!conversationRef.current) {
      toast.error("Chat is not connected yet.");
      return;
    }

    try {
      const builder = conversationRef.current.prepareMessage();
      const trimmed = text?.trim();
      if (trimmed) builder.setBody(trimmed);

      for (const { file } of files) {
        const fd = new FormData();
        fd.set("file", file, file.name);
        builder.addMedia(fd);
      }

      await builder.build().send();
    } catch (err) {
      console.error("[useTwilioChat] Failed to send message with media:", err);
      toast.error("Failed to send message. Please try again.");
    }
  }, []);

  return {
    messages,
    sendMessage,
    sendMessageWithMedia,
    connectionState,
    isLoading,
    hasMoreHistory,
    isLoadingMore,
    loadMoreHistory,
  };
};

export default useTwilioChat;
